import has from 'lodash/has';
import isObject from 'lodash/isObject';

import type { AccessibilityColors } from '@/lib/accessibility/types';
import type { NetworkErrorType } from '@/lib/utils/network-errors';

import { MESSAGE_TYPES } from './widget-events';

export {
  BEACON_EVENTS,
  WIDGET_ANALYTICS_EVENTS,
  WINDOW_EVENTS,
  MESSAGE_TYPES,
  MESSAGE_ACTIONS,
  WIDGET_TRIGGERS,
  type BeaconEvent,
  type WidgetAnalyticsEvent,
  type WidgetTrigger,
} from './widget-events';

export const POST_MESSAGE_TYPES = {
  ...MESSAGE_TYPES,
  ACCESSIBILITY_SYNC: 'ACCESSIBILITY_SYNC',
  ACCESSIBILITY_STATE_REQUEST: 'ACCESSIBILITY_STATE_REQUEST',
  TEXT_TO_SPEECH_REQUEST: 'TEXT_TO_SPEECH_REQUEST',
} as const;

export type PostMessageType = (typeof POST_MESSAGE_TYPES)[keyof typeof POST_MESSAGE_TYPES];

export type WidgetEventAction = 'opened' | 'closed' | 'setComposerText' | 'setTrigger';

export const CHAT_ANALYTICS_EVENTS = {
  CONVERSATION_STARTED: 'conversation_started',
  USER_MESSAGE_SENT: 'user_message_sent',
  RESPONSE_STREAM_STARTED: 'response_stream_started',
  RESPONSE_RETRY_CLICKED: 'response_retry_clicked',
  RESPONSE_AUTO_RETRIED: 'response_auto_retried',
  RESPONSE_COMPLETED: 'response_completed',
  RESPONSE_INTERRUPTED: 'response_interrupted',
  /**
   * Overlaps `response_interrupted` — count one or the other, never their sum. That one is emitted
   * later by response tracking, so it is missing whenever the thread is torn down first; this one
   * is emitted from the error handler and always fires.
   */
  RESPONSE_FAILED: 'response_failed',
  // Resumable (server-persistence) tenants only: a live server-side stream was re-attached after
  // a reload/navigation, replaying its bytes — the resume half of the interrupt→resume funnel.
  RESPONSE_RESUMED: 'response_resumed',
  LINK_CLICKED: 'link_clicked',
  SWITCH_TO_THREAD: 'switch_to_thread',
  CAROUSEL_SHOWN: 'carousel_shown',
  /**
   * A guest acted on a result card without leaving the conversation — the `action` property says
   * which. Deliberately one event for the family: `link_clicked` covers going out to a supplier, and
   * this covers going deeper in, so a new in-card control is a new property value, not a new event.
   */
  CARD_ACTION: 'card_action',
  // These three are in the gate analytics contract: bazak-mastra docs/hitl-approvals.md.
  /** Signed in at a gate that also asks for confirmation. Nothing else records that step, so
   *  without it leaving before sign-in and leaving at the confirmation look the same. */
  TOOL_APPROVAL_SIGNED_IN: 'tool_approval_signed_in',
  /** Once per call, on whichever `channel` showed the confirmation. */
  TOOL_APPROVAL_CONFIRMATION_SHOWN: 'tool_approval_confirmation_shown',
  SIGN_IN_ATTEMPT: 'sign_in_attempt',
  /** The embedded website sign-in funnel: `outcome` is opened, signed_in, abandoned, unavailable or
   *  host_signed_in, `toolName` the protected call it gates. One event, so the funnel is one query. */
  WEBSITE_SIGN_IN: 'website_sign_in',
  DICTATION_STARTED: 'dictation_started',
  DICTATION_ENDED: 'dictation_ended',
  DICTATION_PERMISSION_DENIED: 'dictation_permission_denied',
  DICTATION_ERROR: 'dictation_error',
  TTS_STARTED: 'tts_started',
  TTS_ENDED: 'tts_ended',
  LANGUAGE_SWITCH_SHOWN: 'language_switch_shown',
  LANGUAGE_SWITCH_DECIDED: 'language_switch_decided',
  PII_BLOCK: 'pii_block',
} as const;

export const NETWORK_ANALYTICS_EVENTS = {
  NETWORK_DISCONNECTED: 'network_disconnected',
  NETWORK_RECONNECTED: 'network_reconnected',
} as const;

export type NetworkAnalyticsEvent =
  (typeof NETWORK_ANALYTICS_EVENTS)[keyof typeof NETWORK_ANALYTICS_EVENTS];

export type ChatAnalyticsEvent = (typeof CHAT_ANALYTICS_EVENTS)[keyof typeof CHAT_ANALYTICS_EVENTS];

export const ABORT_REASONS = {
  CONNECTION_ERROR: 'connection_error',
  TIMEOUT: 'timeout',
  SERVER_ERROR: 'server_error',
  USER_CANCELLED: 'user_cancelled',
  PAGE_NAVIGATION: 'page_navigation',
  UNKNOWN: 'unknown',
} as const;

export type AbortReason = (typeof ABORT_REASONS)[keyof typeof ABORT_REASONS];

export const NETWORK_ERROR_TO_ABORT_REASON: Record<NetworkErrorType, AbortReason> = {
  network: ABORT_REASONS.CONNECTION_ERROR,
  timeout: ABORT_REASONS.TIMEOUT,
  server: ABORT_REASONS.SERVER_ERROR,
  chunk: ABORT_REASONS.UNKNOWN,
  abort: ABORT_REASONS.UNKNOWN,
  unknown: ABORT_REASONS.UNKNOWN,
};

export interface WidgetUrlProperties {
  parentUrl?: string;
  parentHost?: string;
  parentPathname?: string;
}

export interface WidgetEventMessage extends WidgetUrlProperties {
  type: typeof POST_MESSAGE_TYPES.WIDGET_EVENT;
  action: WidgetEventAction;
  trigger?: string;
  /** On 'opened': false when the chat opened without the visitor asking. */
  focus?: boolean;
  showButton?: boolean;
  source?: string;
}

export interface NavigateParentMessage {
  type: typeof POST_MESSAGE_TYPES.NAVIGATE_PARENT;
  url: string;
}

export interface OpenInNewTabMessage {
  type: typeof POST_MESSAGE_TYPES.OPEN_IN_NEW_TAB;
  url: string;
}

export type AccessibilityPluginId = 'nagich-accessibility' | 'userway-accessibility';

export interface AccessibilitySyncMessage {
  type: typeof POST_MESSAGE_TYPES.ACCESSIBILITY_SYNC;
  plugin: AccessibilityPluginId;
  classes: string[];
  dataAttrs: Record<string, string>;
  colors?: AccessibilityColors;
}

export interface TextToSpeechRequestMessage {
  type: typeof POST_MESSAGE_TYPES.TEXT_TO_SPEECH_REQUEST;
  text: string;
}

export interface IframeReadyMessage {
  type: typeof POST_MESSAGE_TYPES.IFRAME_READY;
  resolvedContext?: unknown;
}

export interface ParentUrlChangedMessage {
  type: typeof POST_MESSAGE_TYPES.PARENT_URL_CHANGED;
  parentUrl: string;
  domContext?: unknown;
  needsResolved?: boolean;
}

export interface SuggestionContextUpdatedMessage {
  type: typeof POST_MESSAGE_TYPES.SUGGESTION_CONTEXT_UPDATED;
  resolvedContext: unknown;
  failed?: boolean;
  retry?: boolean;
}

// Lives here, not with the refresh itself: the refresh is loaded on demand, so the handler that
// must answer when that load fails cannot reach anything inside it.
export function postSuggestionContext({
  resolvedContext,
  failed,
  retry,
}: {
  resolvedContext: unknown;
  failed?: boolean;
  retry?: boolean;
}): void {
  const message: SuggestionContextUpdatedMessage = {
    type: POST_MESSAGE_TYPES.SUGGESTION_CONTEXT_UPDATED,
    resolvedContext,
    ...(failed && { failed }),
    ...(retry && { retry }),
  };
  window.parent.postMessage(message, '*');
}

export interface ParentPointerUpMessage {
  type: typeof POST_MESSAGE_TYPES.PARENT_POINTER_UP;
}

export interface ParentPointerMoveMessage {
  type: typeof POST_MESSAGE_TYPES.PARENT_POINTER_MOVE;
  clientX: number;
  clientY: number;
}

export interface SetComposerTextMessage {
  type: typeof POST_MESSAGE_TYPES.WIDGET_EVENT;
  action: 'setComposerText';
  text: string;
  submit?: boolean;
  newConversation?: boolean;
  /** False: set the text without focusing the composer. Absent means focus, as before. */
  focus?: boolean;
}

interface CreateMessageGuardOptions<T> {
  type: PostMessageType;
  requiredFields: (keyof T)[];
}

function createMessageGuard<T extends { type: PostMessageType }>({
  type,
  requiredFields,
}: CreateMessageGuardOptions<T>) {
  return (data: unknown): data is T =>
    isObject(data) &&
    has(data, 'type') &&
    (data as { type: unknown }).type === type &&
    requiredFields.every(field => has(data, field as string));
}

export const isWidgetEventMessage = createMessageGuard<WidgetEventMessage>({
  type: POST_MESSAGE_TYPES.WIDGET_EVENT,
  requiredFields: ['action'],
});

export const isNavigateParentMessage = createMessageGuard<NavigateParentMessage>({
  type: POST_MESSAGE_TYPES.NAVIGATE_PARENT,
  requiredFields: ['url'],
});

export const isAccessibilitySyncMessage = createMessageGuard<AccessibilitySyncMessage>({
  type: POST_MESSAGE_TYPES.ACCESSIBILITY_SYNC,
  requiredFields: ['plugin', 'classes'],
});

export const isTextToSpeechRequestMessage = createMessageGuard<TextToSpeechRequestMessage>({
  type: POST_MESSAGE_TYPES.TEXT_TO_SPEECH_REQUEST,
  requiredFields: ['text'],
});

export const isIframeReadyMessage = createMessageGuard<IframeReadyMessage>({
  type: POST_MESSAGE_TYPES.IFRAME_READY,
  requiredFields: [],
});

export const isParentUrlChangedMessage = createMessageGuard<ParentUrlChangedMessage>({
  type: POST_MESSAGE_TYPES.PARENT_URL_CHANGED,
  requiredFields: ['parentUrl'],
});

export const isParentPointerUpMessage = createMessageGuard<ParentPointerUpMessage>({
  type: POST_MESSAGE_TYPES.PARENT_POINTER_UP,
  requiredFields: [],
});

export const isParentPointerMoveMessage = createMessageGuard<ParentPointerMoveMessage>({
  type: POST_MESSAGE_TYPES.PARENT_POINTER_MOVE,
  requiredFields: ['clientX', 'clientY'],
});

export function isSetComposerTextMessage(data: unknown): data is SetComposerTextMessage {
  return (
    isWidgetEventMessage(data) &&
    data.action === 'setComposerText' &&
    typeof (data as SetComposerTextMessage).text === 'string'
  );
}
